import { segment } from '@actions/analytics';
import { TEST_SERIES_KEYS } from '@constants/segment';
import { TEST_SERIES_EVENTS } from '@cont/SeeAll/constants';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useAllTypesOfCourseEventProps from '@hooks/useAllTypesOfCourseEventProps';
import { evtNameCategoryMap, subscriptionTypesObj } from '@constants/index';
import { useRouter } from 'next/router';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMeLatestUserSubscriptions } from '@api/selectors/subscription';
import { getActiveSubscription } from '@models/Me';
import { emptyObject } from '@constants/empty';
import segmentStorage from '@events/segmentStorage';
import useTestSeriesInfo from './useTestSeriesInfo';

const useTestSeriesEvents = (isPurchased) => {
  const { pathname } = useRouter();
  const latestUserSubscriptions = useMyInfo(getMeLatestUserSubscriptions);
  const isStorePage = pathname.includes('store');
  const { data } = useTestSeriesInfo();
  const { goal = {}, isSpecial, isIconic, isSellable } = data;
  const goalSegmentData = useGoalEventProps({ goalUID: goal.uid });
  const { subscription = emptyObject, timeRemaining = 0 } =
    getActiveSubscription(latestUserSubscriptions, goal.uid) || emptyObject;
  const { type } = subscription;
  const segmentData = useAllTypesOfCourseEventProps({
    course: data,
    isTestSeries: true
  });

  const getTestEligibilityType = () => {
    if (isSpecial) {
      return 'Free Test';
    }
    if (isIconic) {
      return 'Iconic Test';
    }
    return 'Plus Test';
  };

  const getIsSubscriptionActive = () => {
    if (isPurchased) {
      return true;
    }
    return timeRemaining > 0;
  };

  const getUserSubscriptionPlan = () => {
    if (isPurchased) {
      return 'Store';
    }
    return subscriptionTypesObj[type];
  };

  const getExtraProps = () => {
    const extraProps = {
      test_eligibility_type: getTestEligibilityType(),
      is_subscription_active: getIsSubscriptionActive(),
      user_subscription_plan: getUserSubscriptionPlan(),
      is_sellable: isSellable,
      last_primary_source: isStorePage ? 'Store' : 'Test Series Page'
    };

    if (isSellable) {
      return { ...extraProps, is_purchased: isPurchased };
    }
    return extraProps;
  };

  const getPostPurchaseEventProps = () => {
    const defaultProps = getExtraProps();
    const lps = segmentStorage.getItem('lps');
    const lpss = segmentStorage.getItem('lpss');

    if (isPurchased) {
      return {
        ...defaultProps,
        last_primary_source: lps ?? 'Goal Home',
        last_primary_source_section: lpss ?? 'N/A'
      };
    }
    return defaultProps;
  };

  const sendPurchaseSegmentEvent = (eventName) => {
    segment.track(
      eventName,
      {
        ...goalSegmentData,
        ...segmentData,
        ...getPostPurchaseEventProps()
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      TEST_SERIES_KEYS
    );
  };

  const sendViewBrochureEvent = () => {
    const eventName = TEST_SERIES_EVENTS.TEST_SERIES_BROCHURE_VIEWED;
    sendPurchaseSegmentEvent(eventName);
  };

  const sendViewSyllabusEvent = () => {
    const eventName = TEST_SERIES_EVENTS.TEST_SERIES_SYLLABUS_VIEWED;
    sendPurchaseSegmentEvent(eventName);
  };

  const sendViewScheduleEvent = () => {
    const eventName = TEST_SERIES_EVENTS.TEST_SERIES_SCHEDULE_VIEWED;
    sendPurchaseSegmentEvent(eventName);
  };

  const sendShareEvent = () => {
    const eventName = TEST_SERIES_EVENTS.TEST_SERIES_SHARED;
    sendPurchaseSegmentEvent(eventName);
  };

  const sendViewedTestSeriesEvent = () => {
    const eventName = TEST_SERIES_EVENTS.TEST_SERIES_VIEWED;
    sendPurchaseSegmentEvent(eventName);
  };
  return {
    sendViewBrochureEvent,
    sendViewSyllabusEvent,
    sendViewScheduleEvent,
    sendShareEvent,
    sendViewedTestSeriesEvent
  };
};

export default useTestSeriesEvents;
